import { forwardRef, useCallback, useMemo, useRef } from 'react'
import { useEvent, useIsomorphicLayoutEffect } from 'react-use'
import { shaderMaterial } from '@react-three/drei'
import {
  extend,
  MeshProps,
  Object3DNode,
  useFrame,
  useThree
} from '@react-three/fiber'
import gsap from 'gsap'
import { Color, Mesh, Scene, ShaderMaterial, Vector2, Vector3 } from 'three'

import { useLenisStore } from '@/contexts/lenis'

export type BackgroundSceneHandler = {
  scene: Scene
}

const BackgroundShaderMaterial = shaderMaterial(
  {
    uColor: new Color('#009FE3') as any,
    uResolution: new Vector2() as any,
    uGradient: new Vector3(0.72, 0.0, 1.0) as any,
    uOpacity: 1,
    uTime: 0
  },
  /* glsl */ `
    varying vec2 vUv;

    void main() {
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);

      vUv = uv;
    }
  `,
  /* glsl */ `
    uniform vec2 uResolution;
    uniform vec3 uGradient;
    uniform vec3 uColor;
    uniform float uOpacity;
    uniform float uTime;

    varying vec2 vUv;

    vec3 sRGBToLinear(vec3 rgb) {
      vec3 low = rgb * (1.0 / 12.92);
      vec3 high = pow((rgb + 0.055) * (1.0 / 1.055), vec3(2.4));
      return vec3(
        rgb.x <= 0.04045 ? low.x : high.x,
        rgb.y <= 0.04045 ? low.y : high.y,
        rgb.z <= 0.04045 ? low.z : high.z
      );
    }
    
    void main() {
      vec2 uv = vUv;
      float time = uTime;

      vec3 baseColor = vec3(1.0, 1.0, 1.0); // #FFFFFF

      /* float noise = texture2D(tNoise, st * 0.1 + vec2(time * 0.075, -time * 0.075)).r;
      float noise = texture2D(tNoise, uv * vec2(0.25, 0.25) + vec2(cos(time * 0.075), sin(time * 0.075))).r;
      noise += texture2D(tNoise, uv * vec2(0.25, 0.25) + vec2(cos(time * 0.075), sin(time * 0.075))).r; */

      /* float noise = texture2D(tNoise, st * 0.05 + vec2(cos(t), cos(-t))).r;
      noise += texture2D(tNoise, st * 0.05 + vec2(sin(-t), sin(t))).g;
      noise = clamp(noise, 0.0, 0.8); */

      float ratio = uResolution.x / uResolution.y;
      uv.x *= ratio;

      vec2 center = vec2(0.5, 0.5);
      center.x *= ratio;

      center.x += 0.8 * cos(time * 0.4);
      center.y += 0.6 * sin(time * 0.4);

      float dist = length(uv - center) * uGradient.x;

      float gradient = smoothstep(uGradient.y, uGradient.z, dist);

      vec3 color = mix(uColor, baseColor, gradient);
      color = sRGBToLinear(color);

      float opacity = uOpacity;

      gl_FragColor = vec4(color, opacity);

      #include <tonemapping_fragment>
	    #include <colorspace_fragment>
    }
  `
)

declare module '@react-three/fiber' {
  // eslint-disable-next-line no-unused-vars
  interface ThreeElements {
    backgroundShaderMaterial: Object3DNode<
      ShaderMaterial,
      typeof BackgroundShaderMaterial
    >
  }
}

const Background = forwardRef<Mesh, MeshProps>((props, ref) => {
  useMemo(() => extend({ BackgroundShaderMaterial }), [])

  const material = useRef<ShaderMaterial>(null!)
  const timeline = useRef<gsap.core.Timeline>(null!)
  const get = useThree((state) => state.get)

  const resize = useCallback(() => {
    const canvas = get().gl.domElement
    material.current.uniforms.uResolution.value.set(
      canvas.clientWidth,
      canvas.clientHeight
    )
  }, [get])

  useFrame(({ clock }) => {
    material.current.uniforms.uTime.value = clock.getElapsedTime()

    timeline.current.progress(useLenisStore.getState().progress)
  })

  useEvent('resize', resize)

  useIsomorphicLayoutEffect(() => resize(), [])

  useIsomorphicLayoutEffect(() => {
    const target = { color: '#009FE3', opacity: 1 }
    timeline.current = gsap
      .timeline({
        paused: true,
        defaults: { duration: 1, ease: 'none' },
        onUpdate: () => {
          material.current.uniforms.uOpacity.value = target.opacity * 0.9
          material.current.uniforms.uColor.value
            .set(target.color)
            .convertLinearToSRGB()
        }
      })
      .to(target, { color: '#009FE3', opacity: 1 })
      .to(target, { color: '#99CC33', opacity: 1 })
      .to(target, { color: '#009FE3', opacity: 1 })
      .to(target, { color: '#99CC33', opacity: 1 })
      .to(target, { color: '#009FE3', opacity: 1 })
      .to(target, { color: '#dddddd', opacity: 1 })
      .to(target, { color: '#ffffff', opacity: 1 })
  }, [])

  return (
    <mesh ref={ref as any} {...props}>
      <planeGeometry args={[1, 1]} />
      <backgroundShaderMaterial ref={material} transparent={true} />
    </mesh>
  )
})

// useTexture.preload(NOISE_PATH)

export default Background
